import { useEffect, useState } from 'react';
import { Start } from './components/Start';
import { Rundgang } from './components/Rundgang';
import { Zaehler } from './components/Zaehler';
import { Schluessel } from './components/Schluessel';
import { Unterschriften } from './components/Unterschriften';
import { Abschluss } from './components/Abschluss';
import { Uebersicht } from './components/Uebersicht';
import { Vergleich } from './components/Vergleich';
import { Fortschritt, type FortschrittSchritt } from './components/Fortschritt';
import {
	beendeAktuellesProtokoll,
	ladeAktuelleProtokollId,
	ladeObjekte,
	ladeProtokolle,
	ladeSchritt,
	speichereAktuelleProtokollId,
	speichereObjekte,
	speichereProtokoll,
	speichereSchritt
} from './speicher';
import { exportiereAlles, importiereDatei } from './backup';
import { DokumentIcon, HausIcon, SchluesselIcon, UnterschriftIcon, ZaehlerIcon } from './icons';
import { neuesObjekt, type Objekt, type Protokoll } from './domain/types';

type SchrittKey = 'rundgang' | 'zaehler' | 'schluessel' | 'unterschriften' | 'abschluss';
type Ansicht = 'uebersicht' | 'start';

const SCHRITTE: (FortschrittSchritt & { key: SchrittKey })[] = [
	{ key: 'rundgang', label: 'Räume', icon: <HausIcon width={16} height={16} /> },
	{ key: 'zaehler', label: 'Zähler', icon: <ZaehlerIcon width={16} height={16} /> },
	{ key: 'schluessel', label: 'Schlüssel', icon: <SchluesselIcon width={16} height={16} /> },
	{ key: 'unterschriften', label: 'Unterschrift', icon: <UnterschriftIcon width={16} height={16} /> },
	{ key: 'abschluss', label: 'Dokument', icon: <DokumentIcon width={16} height={16} /> }
];

function istSchrittKey(wert: string): wert is SchrittKey {
	return SCHRITTE.some((s) => s.key === wert);
}

function indexVon(schritt: SchrittKey): number {
	return SCHRITTE.findIndex((s) => s.key === schritt);
}

interface VergleichsAuswahl {
	objekt: Objekt;
	einzug: Protokoll;
	auszug: Protokoll;
}

export function App() {
	const [objekte, setObjekte] = useState<Objekt[]>(() => ladeObjekte());
	const [protokolle, setProtokolle] = useState<Protokoll[]>(() => ladeProtokolle());
	const [aktuelleProtokollId, setAktuelleProtokollId] = useState<string | null>(() => ladeAktuelleProtokollId());
	const [schritt, setSchritt] = useState<SchrittKey>(() => {
		const gespeichert = ladeSchritt();
		return gespeichert && istSchrittKey(gespeichert) ? gespeichert : 'rundgang';
	});
	const [maxIndex, setMaxIndex] = useState(() => indexVon(schritt));
	const [ansicht, setAnsicht] = useState<Ansicht>(() => (ladeObjekte().length === 0 ? 'start' : 'uebersicht'));
	const [startVorlage, setStartVorlage] = useState<Objekt | undefined>(undefined);
	const [vergleich, setVergleich] = useState<VergleichsAuswahl | null>(null);

	const aktuellesProtokoll = protokolle.find((p) => p.id === aktuelleProtokollId) ?? null;

	useEffect(() => {
		if (aktuellesProtokoll) speichereSchritt(schritt);
	}, [schritt, aktuellesProtokoll]);

	function gehZu(neuerSchritt: SchrittKey) {
		setSchritt(neuerSchritt);
		setMaxIndex((bisher) => Math.max(bisher, indexVon(neuerSchritt)));
	}

	function aendereAktuellesProtokoll(update: Protokoll) {
		const neu = speichereProtokoll(update);
		setProtokolle(neu);
	}

	function starten(neu: Protokoll) {
		let zielObjekte = objekte;
		let objektId = neu.objektId;

		if (!objektId) {
			const objekt = neuesObjekt(neu.objekt);
			objektId = objekt.id;
			zielObjekte = [...objekte, objekt];
			setObjekte(zielObjekte);
			speichereObjekte(zielObjekte);
		}

		const protokollMitObjekt: Protokoll = { ...neu, objektId };
		setProtokolle(speichereProtokoll(protokollMitObjekt));
		setAktuelleProtokollId(protokollMitObjekt.id);
		speichereAktuelleProtokollId(protokollMitObjekt.id);
		setSchritt('rundgang');
		setMaxIndex(0);
		speichereSchritt('rundgang');
	}

	function oeffneProtokoll(protokoll: Protokoll) {
		setAktuelleProtokollId(protokoll.id);
		speichereAktuelleProtokollId(protokoll.id);
		// Aus der Übersicht geöffnet heißt: nochmal ansehen/weiterbearbeiten,
		// nicht von vorn anfangen — der ganze Stepper ist sofort erreichbar.
		setMaxIndex(SCHRITTE.length - 1);
		setSchritt('abschluss');
		speichereSchritt('abschluss');
	}

	function zurueckZurUebersicht() {
		beendeAktuellesProtokoll();
		setAktuelleProtokollId(null);
		setAnsicht('uebersicht');
	}

	function neuesObjektStarten() {
		setStartVorlage(undefined);
		setAnsicht('start');
	}

	function neueUebergabeFuerObjekt(objekt: Objekt) {
		setStartVorlage(objekt);
		setAnsicht('start');
	}

	async function importieren(datei: File) {
		const ergebnis = await importiereDatei(datei);
		if (!ergebnis.erfolg) {
			window.alert(ergebnis.fehler ?? 'Import fehlgeschlagen.');
			return;
		}
		setObjekte(ladeObjekte());
		setProtokolle(ladeProtokolle());
		window.alert(
			`Wiederhergestellt: ${ergebnis.neueObjekte ?? 0} Objekt(e), ${ergebnis.neueProtokolle ?? 0} Protokoll(e).`
		);
	}

	if (vergleich) {
		return (
			<Vergleich
				objekt={vergleich.objekt}
				einzug={vergleich.einzug}
				auszug={vergleich.auszug}
				onZurueck={() => setVergleich(null)}
			/>
		);
	}

	if (!aktuellesProtokoll) {
		if (ansicht === 'start') {
			return (
				<Start
					onStart={starten}
					objekt={startVorlage}
					onZurueck={objekte.length > 0 ? () => setAnsicht('uebersicht') : undefined}
				/>
			);
		}
		return (
			<Uebersicht
				objekte={objekte}
				protokolle={protokolle}
				onNeuesObjekt={neuesObjektStarten}
				onNeueUebergabe={neueUebergabeFuerObjekt}
				onOeffneProtokoll={oeffneProtokoll}
				onVergleich={(objekt, einzug, auszug) => setVergleich({ objekt, einzug, auszug })}
				onExport={exportiereAlles}
				onImport={importieren}
			/>
		);
	}

	const aktiverIndex = indexVon(schritt);

	function springeZu(index: number) {
		if (index <= maxIndex) gehZu(SCHRITTE[index].key);
	}

	function weiter() {
		if (aktiverIndex < SCHRITTE.length - 1) gehZu(SCHRITTE[aktiverIndex + 1].key);
	}

	function zurueck() {
		if (aktiverIndex > 0) gehZu(SCHRITTE[aktiverIndex - 1].key);
	}

	return (
		<div className="bildschirm bildschirm--ablauf">
			<header className="ablauf-kopf">
				<div className="ablauf-kopf-titel">
					<strong>{aktuellesProtokoll.objekt.strasse || 'Ohne Adresse'}</strong>
					<span className="ablauf-kopf-art">{aktuellesProtokoll.art === 'einzug' ? 'Einzug' : 'Auszug'}</span>
				</div>
				<span className="ablauf-speicherstatus">
					<i />
					Lokal gespeichert
				</span>
			</header>

			<Fortschritt schritte={SCHRITTE} aktiverIndex={aktiverIndex} maxErreichterIndex={maxIndex} onSpringeZu={springeZu} />

			<div className="ablauf-inhalt">
				{schritt === 'rundgang' && (
					<Rundgang protokoll={aktuellesProtokoll} onAendern={aendereAktuellesProtokoll} onWeiter={weiter} />
				)}
				{schritt === 'zaehler' && (
					<Zaehler
						protokoll={aktuellesProtokoll}
						onAendern={aendereAktuellesProtokoll}
						onWeiter={weiter}
						onZurueck={zurueck}
					/>
				)}
				{schritt === 'schluessel' && (
					<Schluessel
						protokoll={aktuellesProtokoll}
						onAendern={aendereAktuellesProtokoll}
						onWeiter={weiter}
						onZurueck={zurueck}
					/>
				)}
				{schritt === 'unterschriften' && (
					<Unterschriften
						protokoll={aktuellesProtokoll}
						onAendern={aendereAktuellesProtokoll}
						onWeiter={weiter}
						onZurueck={zurueck}
					/>
				)}
				{schritt === 'abschluss' && (
					<Abschluss
						protokoll={aktuellesProtokoll}
						onAendern={aendereAktuellesProtokoll}
						onZurueck={zurueck}
						onZurueckZurUebersicht={zurueckZurUebersicht}
					/>
				)}
			</div>
		</div>
	);
}
